// src/data/mock/martyrsTeam.ts

/**
 * Martyrs Team Data Structure
 * 
 * This file contains data for team members (coaches, staff, administrators)
 * who lost their lives during the conflict.
 * 
 * Content is purely humanitarian and serves to honor their memory and legacy.
 * 
 * @note Replace placeholder data with actual information when available
 * @note All images should be placed in /public/images/martyrs/ directory
 */

export interface Martyr {
  id: string;
  
  // Basic Information
  name: {
    en: string;
    ar: string;
  };
  
  role: {
    en: string;
    ar: string;
  };
  
  // Years Format: "Birth Year - Death Year" (e.g., "1985 - 2023")
  years: string;
  
  // Date of passing in ISO format (YYYY-MM-DD) for precise memorial dates
  dateOfPassing?: string;
  
  // Image path (relative to public directory)
  // Recommended: /images/martyrs/[id].jpg
  image?: string;
  
  // Short biography (2-3 sentences)
  bio?: {
    en: string;
    ar: string;
  };
  
  // Detailed tribute/legacy statement (used in expanded view)
  tribute?: {
    en: string;
    ar: string;
  };
  
  // List of key achievements or contributions
  achievements?: {
    en: string[];
    ar: string[];
  };
  
  // A memorable quote by or about the person
  quote?: {
    en: string;
    ar: string;
  };
  
  // Optional: Sport specialty (if applicable)
  sport?: {
    en: string;
    ar: string;
  };
  
  // Optional: Years of service at Champions Academy
  yearsOfService?: number;
}

/**
 * Martyrs Team Data
 * 
 * TODO: Replace with actual data when available
 * - Add real names, roles, and dates
 * - Upload actual photos to /public/images/martyrs/
 * - Verify all information for accuracy
 * - Add achievements and quotes
 * - Ensure translations are accurate
 */
export const martyrsTeam: Martyr[] = [
  // ==========================================
  // COACHES - المدربون
  // ==========================================
  {
    id: "mt-coach-001",
    name: { 
      en: "Coach Name Placeholder", 
      ar: "اسم المدرب المؤقت" 
    },
    role: { 
      en: "Head Football Coach", 
      ar: "المدرب الرئيسي لكرة القدم" 
    },
    years: "1985 - 2023",
    dateOfPassing: "2023-10-15",
    image: "/images/martyrs/mt-coach-001.jpg",
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    yearsOfService: 8,
    bio: {
      en: "A dedicated coach who inspired countless young athletes with passion and commitment. Known for developing both athletic skills and strong character in every player.",
      ar: "مدرب متفانٍ ألهم عدداً لا يحصى من الرياضيين الشباب بشغفه والتزامه. معروف بتطوير المهارات الرياضية والشخصية القوية في كل لاعب."
    },
    tribute: {
      en: "Forever remembered for building champions on and off the field. His legacy lives on in every player he coached and every life he touched.",
      ar: "سيُذكر إلى الأبد لبناء أبطال داخل وخارج الملعب. إرثه يعيش في كل لاعب درّبه وكل حياة لمسها."
    },
    achievements: {
      en: [
        "Led team to 3 regional championships",
        "Trained over 500 young athletes",
        "Developed youth training program adopted nationwide",
        "Mentored 15 players who became professional athletes",
        "Received Coach of the Year award (2021)"
      ],
      ar: [
        "قاد الفريق لـ 3 بطولات إقليمية",
        "درّب أكثر من 500 رياضي شاب",
        "طوّر برنامج تدريب الشباب المعتمد على الصعيد الوطني",
        "أرشد 15 لاعباً أصبحوا رياضيين محترفين",
        "حصل على جائزة مدرب العام (2021)"
      ]
    },
    quote: {
      en: "Every child deserves a chance to be a champion, not just in sports, but in life.",
      ar: "كل طفل يستحق فرصة ليكون بطلاً، ليس فقط في الرياضة، بل في الحياة."
    }
  },
  {
    id: "mt-coach-002",
    name: { 
      en: "Coach Name Placeholder", 
      ar: "اسم المدرب المؤقت" 
    },
    role: { 
      en: "Swimming Instructor", 
      ar: "مدرب سباحة" 
    },
    years: "1990 - 2023",
    dateOfPassing: "2023-11-03",
    image: "/images/martyrs/mt-coach-002.jpg",
    sport: {
      en: "Swimming",
      ar: "السباحة"
    },
    yearsOfService: 6,
    bio: {
      en: "A champion swimmer who dedicated their life to teaching the next generation. Their patience and expertise helped hundreds of children overcome their fear of water.",
      ar: "سباح بطل كرس حياته لتعليم الجيل القادم. صبره وخبرته ساعدا المئات من الأطفال على التغلب على خوفهم من الماء."
    },
    tribute: {
      en: "Their legacy lives on in every child who learned to swim, every fear overcome, every dream achieved in the water.",
      ar: "إرثه يعيش في كل طفل تعلم السباحة، كل خوف تم التغلب عليه، كل حلم تحقق في الماء."
    },
    achievements: {
      en: [
        "National swimming champion (2015, 2017)",
        "Taught swimming to over 800 children",
        "Developed water safety program",
        "Trained 5 national team swimmers",
        "Founded 'Swim for Hope' initiative"
      ],
      ar: [
        "بطل سباحة وطني (2015، 2017)",
        "علّم السباحة لأكثر من 800 طفل",
        "طوّر برنامج السلامة المائية",
        "درّب 5 سباحين للمنتخب الوطني",
        "أسس مبادرة 'اسبح من أجل الأمل'"
      ]
    },
    quote: {
      en: "In the water, we are all equal. It teaches us courage, discipline, and respect.",
      ar: "في الماء، نحن جميعاً متساوون. إنه يعلمنا الشجاعة والانضباط والاحترام."
    }
  },
  {
    id: "mt-coach-003",
    name: { 
      en: "Coach Name Placeholder", 
      ar: "اسم المدرب المؤقت" 
    },
    role: { 
      en: "Basketball Coach", 
      ar: "مدرب كرة السلة" 
    },
    years: "1988 - 2023",
    dateOfPassing: "2023-10-28",
    image: "/images/martyrs/mt-coach-003.jpg",
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    yearsOfService: 10,
    bio: {
      en: "A visionary coach who transformed our basketball program into one of the region's finest. Known for their strategic mind and ability to bring out the best in every player.",
      ar: "مدرب صاحب رؤية حوّل برنامج كرة السلة لدينا إلى واحد من أفضل البرامج في المنطقة. معروف بعقله الاستراتيجي وقدرته على إخراج أفضل ما في كل لاعب."
    },
    achievements: {
      en: [
        "Built championship-winning basketball program",
        "Coached over 400 young athletes",
        "Developed innovative training methods",
        "Mentored 10 professional basketball players"
      ],
      ar: [
        "بنى برنامج كرة سلة فائز بالبطولات",
        "درّب أكثر من 400 رياضي شاب",
        "طوّر أساليب تدريب مبتكرة",
        "أرشد 10 لاعبي كرة سلة محترفين"
      ]
    },
    quote: {
      en: "Basketball is more than a game; it's a lesson in teamwork, perseverance, and believing in yourself.",
      ar: "كرة السلة أكثر من مجرد لعبة؛ إنها درس في العمل الجماعي والمثابرة والإيمان بالنفس."
    }
  },

  // ==========================================
  // ADMINISTRATIVE STAFF - الطاقم الإداري
  // ==========================================
  {
    id: "mt-admin-001",
    name: { 
      en: "Staff Name Placeholder", 
      ar: "اسم الموظف المؤقت" 
    },
    role: { 
      en: "Academy Director", 
      ar: "مدير الأكاديمية" 
    },
    years: "1975 - 2023",
    dateOfPassing: "2023-10-10",
    image: "/images/martyrs/mt-admin-001.jpg",
    yearsOfService: 12,
    bio: {
      en: "A visionary leader who guided Champions Academy through its most challenging times. Their dedication to children's development went beyond sports to encompass education and character building.",
      ar: "قائد صاحب رؤية وجّه أكاديمية تشامبيونز خلال أصعب أوقاتها. تفانيه في تطوير الأطفال تجاوز الرياضة ليشمل التعليم وبناء الشخصية."
    },
    tribute: {
      en: "A pillar of strength whose vision shaped thousands of young lives. The academy stands as a testament to their unwavering commitment.",
      ar: "ركيزة قوة شكلت رؤيته آلاف الحياة الشابة. الأكاديمية تقف شاهداً على التزامه الثابت."
    },
    achievements: {
      en: [
        "Established Champions Academy's humanitarian programs",
        "Expanded academy to serve 2000+ children",
        "Created scholarship program for underprivileged youth",
        "Built partnerships with international sports organizations",
        "Received Community Service Award (2020)"
      ],
      ar: [
        "أسس البرامج الإنسانية لأكاديمية تشامبيونز",
        "وسّع الأكاديمية لخدمة أكثر من 2000 طفل",
        "أنشأ برنامج منح دراسية للشباب المحرومين",
        "بنى شراكات مع منظمات رياضية دولية",
        "حصل على جائزة خدمة المجتمع (2020)"
      ]
    },
    quote: {
      en: "Our mission is not just to create athletes, but to build strong, compassionate human beings.",
      ar: "مهمتنا ليست فقط خلق رياضيين، بل بناء بشر أقوياء ورحماء."
    }
  },
  {
    id: "mt-admin-002",
    name: { 
      en: "Staff Name Placeholder", 
      ar: "اسم الموظف المؤقت" 
    },
    role: { 
      en: "Administrative Coordinator", 
      ar: "منسق إداري" 
    },
    years: "1982 - 2023",
    dateOfPassing: "2023-11-15",
    image: "/images/martyrs/mt-admin-002.jpg",
    yearsOfService: 7,
    bio: {
      en: "The heart of our administration, whose kindness and efficiency kept the academy running smoothly. Every child and parent knew they could count on their support.",
      ar: "قلب إدارتنا، الذي أبقى لطفه وكفاءته الأكاديمية تعمل بسلاسة. كل طفل ووالد يعلم أنه يمكن الاعتماد على دعمه."
    },
    tribute: {
      en: "A true servant of the sports community whose warmth and dedication touched countless lives. Their organizational excellence kept our mission alive.",
      ar: "خادم حقيقي للمجتمع الرياضي الذي لمس دفؤه وتفانيه حياة لا تعد ولا تحصى. تميزه التنظيمي أبقى رسالتنا حية."
    },
    achievements: {
      en: [
        "Streamlined academy operations and registration",
        "Supported over 1500 families through enrollment process",
        "Coordinated major sports events and tournaments",
        "Managed scholarship distribution program"
      ],
      ar: [
        "حسّن عمليات الأكاديمية والتسجيل",
        "دعم أكثر من 1500 عائلة خلال عملية التسجيل",
        "نسّق فعاليات رياضية كبرى وبطولات",
        "أدار برنامج توزيع المنح الدراسية"
      ]
    },
    quote: {
      en: "Behind every successful athlete is a support system that believes in them.",
      ar: "خلف كل رياضي ناجح نظام دعم يؤمن به."
    }
  },

  // ==========================================
  // SUPPORT STAFF - طاقم الدعم
  // ==========================================
  {
    id: "mt-support-001",
    name: { 
      en: "Staff Name Placeholder", 
      ar: "اسم الموظف المؤقت" 
    },
    role: { 
      en: "Sports Medicine Specialist", 
      ar: "أخصائي طب رياضي" 
    },
    years: "1987 - 2023",
    dateOfPassing: "2023-10-20",
    image: "/images/martyrs/mt-support-001.jpg",
    yearsOfService: 5,
    bio: {
      en: "A dedicated healthcare professional who ensured the safety and wellbeing of all our athletes. Their expertise prevented countless injuries and helped athletes reach their full potential.",
      ar: "محترف رعاية صحية متفانٍ ضمن سلامة ورفاهية جميع رياضيينا. خبرته منعت إصابات لا تعد ولا تحصى وساعدت الرياضيين على الوصول إلى إمكاناتهم الكاملة."
    },
    achievements: {
      en: [
        "Implemented comprehensive injury prevention program",
        "Provided care for over 1000 athletes",
        "Conducted health and fitness workshops",
        "Developed athlete nutrition guidelines"
      ],
      ar: [
        "نفّذ برنامج شامل لمنع الإصابات",
        "قدّم الرعاية لأكثر من 1000 رياضي",
        "أجرى ورش عمل صحية ولياقة بدنية",
        "طوّر إرشادات تغذية الرياضيين"
      ]
    },
    quote: {
      en: "A healthy athlete is a happy athlete. Prevention is always better than cure.",
      ar: "الرياضي الصحي رياضي سعيد. الوقاية دائماً أفضل من العلاج."
    }
  },
  {
    id: "mt-support-002",
    name: { 
      en: "Staff Name Placeholder", 
      ar: "اسم الموظف المؤقت" 
    },
    role: { 
      en: "Equipment Manager", 
      ar: "مدير المعدات" 
    },
    years: "1980 - 2023",
    dateOfPassing: "2023-11-08",
    image: "/images/martyrs/mt-support-002.jpg",
    yearsOfService: 9,
    bio: {
      en: "The guardian of our sports equipment who ensured every child had what they needed to train and compete. Their meticulous care kept our facilities in top condition.",
      ar: "حارس معداتنا الرياضية الذي ضمن أن كل طفل لديه ما يحتاجه للتدريب والمنافسة. عنايته الدقيقة أبقت مرافقنا في أفضل حالة."
    },
    achievements: {
      en: [
        "Maintained and organized all sports equipment",
        "Ensured safety standards for all equipment",
        "Managed equipment donations and distribution",
        "Trained staff on proper equipment care"
      ],
      ar: [
        "حافظ على وتنظيم جميع المعدات الرياضية",
        "ضمن معايير السلامة لجميع المعدات",
        "أدار تبرعات وتوزيع المعدات",
        "درّب الطاقم على الرعاية السليمة للمعدات"
      ]
    }
  },

  // ==========================================
  // ADDITIONAL PLACEHOLDERS
  // ==========================================
  {
    id: "mt-placeholder-001",
    name: { 
      en: "Name Placeholder", 
      ar: "اسم مؤقت" 
    },
    role: { 
      en: "Position Placeholder", 
      ar: "منصب مؤقت" 
    },
    years: "19XX - 2023",
    dateOfPassing: "2023-10-XX",
    image: "/images/martyrs/placeholder.jpg",
    yearsOfService: 0,
    bio: {
      en: "Biography placeholder - to be filled with actual information.",
      ar: "نبذة مؤقتة - سيتم ملؤها بالمعلومات الفعلية."
    },
    tribute: {
      en: "Tribute placeholder - to be filled with actual information.",
      ar: "تكريم مؤقت - سيتم ملؤه بالمعلومات الفعلية."
    }
  }
];

/**
 * Utility Functions
 */

// Get martyrs by role
export const getMartyrsByRole = (role: string): Martyr[] => {
  return martyrsTeam.filter(martyr => 
    martyr.role.en.toLowerCase().includes(role.toLowerCase())
  );
};

// Get martyrs count
export const getMartyrsCount = (): number => {
  return martyrsTeam.length;
};

// Get martyrs by sport
export const getMartyrsBySport = (sport: string): Martyr[] => {
  return martyrsTeam.filter(martyr => 
    martyr.sport?.en.toLowerCase().includes(sport.toLowerCase())
  );
};

// Sort martyrs by years of service
export const sortByYearsOfService = (): Martyr[] => {
  return [...martyrsTeam].sort((a, b) => 
    (b.yearsOfService || 0) - (a.yearsOfService || 0)
  );
};

/**
 * Data Validation Helper
 */
export const validateMartyrData = (martyr: Martyr): boolean => {
  const required = [
    martyr.id,
    martyr.name.en,
    martyr.name.ar,
    martyr.role.en,
    martyr.role.ar,
    martyr.years
  ];
  
  return required.every(field => field && field.trim() !== '');
};

/**
 * Export Statistics
 */
export const martyrsStatistics = {
  total: martyrsTeam.length,
  coaches: martyrsTeam.filter(m => m.role.en.toLowerCase().includes('coach')).length,
  staff: martyrsTeam.filter(m => !m.role.en.toLowerCase().includes('coach')).length,
  totalYearsOfService: martyrsTeam.reduce((sum, m) => sum + (m.yearsOfService || 0), 0)
};
